export class ThumbnailCache {
  private static instance: ThumbnailCache | null = null;
  private cache = new Map<string, string>(); // Map<instanceUID, blobURL>
  private maxSize: number;
  private urlRefCounts = new Map<string, number>(); // Track how many times a URL is referenced

  private constructor(maxSize: number = 2000) {
    this.maxSize = maxSize;
  }

  static getInstance(maxSize: number = 2000): ThumbnailCache {
    if (!ThumbnailCache.instance) {
      ThumbnailCache.instance = new ThumbnailCache(maxSize);
    }
    return ThumbnailCache.instance;
  }

  get(key: string): string | undefined {
    const value = this.cache.get(key);
    if (value) {
      // Re-insert to mark as most recently used
      this.cache.delete(key);
      this.cache.set(key, value);
      // Increment reference count
      this.urlRefCounts.set(value, (this.urlRefCounts.get(value) || 0) + 1);
    }
    return value;
  }

  set(key: string, value: string): void {
    if (this.cache.has(key)) {
      const oldValue = this.cache.get(key);
      if (oldValue && oldValue !== value) {
        this.decrementRefCount(oldValue);
      }
      this.cache.delete(key);
    }
    else if (this.cache.size >= this.maxSize) {
      // Delete least recently used (first inserted)
      const oldestKey = this.cache.keys().next().value;

      if (!oldestKey) return; // Safety check

      const oldURL = this.cache.get(oldestKey);
      if (oldURL) {
        this.decrementRefCount(oldURL);
      }
      this.cache.delete(oldestKey);
    }
    this.cache.set(key, value);
    this.urlRefCounts.set(value, (this.urlRefCounts.get(value) || 0) + 1);
  }

  setMaxSize(newMaxSize: number): void {
    this.maxSize = newMaxSize;
    // If current size exceeds new max size, remove oldest entries
    while (this.cache.size > this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (!oldestKey) break;

      const oldURL = this.cache.get(oldestKey);
      if (oldURL) {
        this.decrementRefCount(oldURL);
      }
      this.cache.delete(oldestKey);
    }
  }

  private decrementRefCount(url: string): void {
    const count = this.urlRefCounts.get(url) || 0;
    if (count <= 1) {
      URL.revokeObjectURL(url);
      this.urlRefCounts.delete(url);
    }
    else {
      this.urlRefCounts.set(url, count - 1);
    }
  }

  clear(): void {
    // Revoke all URLs
    Array.from(this.cache.values()).forEach(url => URL.revokeObjectURL(url));
    this.cache.clear();
    this.urlRefCounts.clear();
  }
}
